import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import type { TeamRole } from '../../types/auth';
import { hasAnyPermission, hasPermission } from '../../utils/permissions';

interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
  requireRoles?: TeamRole[];
  requirePermissions?: string[];
  requireAnyPermissions?: string[];
}

export function ProtectedRoute({ 
  children, 
  fallback,
  requireRoles,
  requirePermissions,
  requireAnyPermissions,
}: ProtectedRouteProps) {
  const { isAuthenticated, role, permissions, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      )
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate 
        to="/login" 
        state={{ from: location.pathname }} 
        replace 
      />
    );
  }

  // Check admin requirement
  const lacksRequiredPermission = requirePermissions?.some(
    (permission) => !hasPermission(permissions, permission),
  );
  const lacksAnyPermission =
    requireAnyPermissions && !hasAnyPermission(permissions, requireAnyPermissions);

  if (
    (requireRoles && (!role || !requireRoles.includes(role))) ||
    lacksRequiredPermission ||
    lacksAnyPermission
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
